/**
 * wave-noise.js — Ozlo noise wave
 *
 * Vertical equalizer bars:
 * - Outer 10% left/right: flat
 * - Centre 80%: aggressive, random, noisy
 * - Amplitude reduced ~12% from previous version
 *
 * API: new OzloWaveNoise(canvas) → .start() / .stop() / .destroy()
 */
(function (global) {
  'use strict';

  class OzloWaveNoise {
    constructor(canvas) {
      this._canvas  = canvas;
      this._ctx     = canvas.getContext('2d');
      this._raf     = null;
      this._running = false;
      this._t       = 0;
      this._bars    = [];
      this._built   = false;

      this._resize = this._resize.bind(this);
      this._ro = new ResizeObserver(() => { this._resize(); this._built = false; });
      this._ro.observe(canvas);
      this._resize();
    }

    start() {
      if (this._running) return;
      this._running = true;
      this._loop();
    }

    stop() {
      this._running = false;
      if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null; }
      this._ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
    }

    destroy() { this.stop(); this._ro.disconnect(); }

    _resize() {
      const dpr = window.devicePixelRatio || 1;
      const r   = this._canvas.getBoundingClientRect();
      this._canvas.width  = r.width  * dpr;
      this._canvas.height = r.height * dpr;
    }

    _buildBars(W, H) {
      const barW  = 3;
      const gap   = 3.5;
      const step  = barW + gap;
      const count = Math.floor(W / step);
      const startX = (W - count * step) / 2;

      this._bars = [];

      for (let i = 0; i < count; i++) {
        const xFrac = i / (count - 1);

        let zoneMax;
        if (xFrac < 0.10) {
          zoneMax = 0.06;
        } else if (xFrac < 0.20) {
          zoneMax = 0.06 + 0.94 * ((xFrac - 0.10) / 0.10);
        } else if (xFrac > 0.90) {
          zoneMax = 0.06;
        } else if (xFrac > 0.80) {
          zoneMax = 0.06 + 0.94 * ((0.90 - xFrac) / 0.10);
        } else {
          zoneMax = 1.0;
        }

        const seed  = i * 1.6180339887 + 0.31;
        // Reduced from H*0.46 → H*0.40 (~13% reduction)
        const baseH = (0.25 + 0.75 * Math.abs(Math.sin(seed * 4.1))) * zoneMax * (H * 0.36);

        const isEdge = zoneMax < 0.15;
        const speed  = isEdge
          ? 0.005 + 0.005 * Math.abs(Math.sin(seed))
          : 0.045 + 0.090 * Math.abs(Math.sin(seed * 1.7));

        this._bars.push({
          x:          startX + i * step + barW / 2,
          baseH,
          speed,
          phase:      seed * 2.39996,
          groupPhase: xFrac * Math.PI * 4,
          zoneMax,
          isEdge,
        });
      }
      this._built = true;
    }

    _loop() {
      if (!this._running) return;
      const dpr = window.devicePixelRatio || 1;
      const W   = this._canvas.width  / dpr;
      const H   = this._canvas.height / dpr;
      const ctx = this._ctx;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);

      if (!this._built) this._buildBars(W, H);

      const t   = this._t;
      const cy  = H * 0.5;
      const barW = 3;

      for (const bar of this._bars) {
        let h;
        if (bar.isEdge) {
          h = bar.baseH * (0.3 + 0.7 * Math.abs(Math.sin(bar.phase + t * bar.speed)));
        } else {
          const s1 = Math.abs(Math.sin(bar.phase       + t * bar.speed));
          const s2 = Math.abs(Math.sin(bar.phase * 1.3 + t * bar.speed * 1.618));
          const s3 = Math.abs(Math.sin(bar.phase * 0.7 + t * bar.speed * 2.414));
          const spike = s1 * s1 * (0.6 + 0.4 * s2);
          const base  = (s1 * 0.5 + s2 * 0.3 + s3 * 0.2);
          h = bar.baseH * Math.max(0.08, spike * 0.6 + base * 0.4);
        }

        // Clean solid bar only — no glow, no shadow
        ctx.fillStyle = 'rgba(255,255,255,0.88)';
        ctx.beginPath();
        ctx.roundRect(bar.x - barW * 0.5, cy - h, barW, h * 2, 2);
        ctx.fill();
      }

      this._t += 1;
      this._raf = requestAnimationFrame(() => this._loop());
    }
  }

  global.OzloWaveNoise = OzloWaveNoise;
}(window));